import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const AdminFeedback = () => {
  const navigate = useNavigate();

  // === Core Data States ===
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  // === UI States ===
  const [activeTab, setActiveTab] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseText, setResponseText] = useState('');

  useEffect(() => {
    loadAllData();
  }, []);

  // ========================= DATA FETCHING =========================
  const loadAllData = async () => {
    setLoading(true);
    try {
      const [feedbackRes, statsRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/feedback`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/feedback/stats`)
      ]);

      const feedbackData = await feedbackRes.json();
      const statsData = await statsRes.json();

      setFeedback(feedbackData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ========================= ACTIONS =========================
  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.FEEDBACK}/${feedbackId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        loadAllData();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleRespond = async () => {
    if (!responseText.trim()) {
      alert('Please enter a response');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.FEEDBACK}/${selectedFeedback.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adminResponse: responseText,
          status: 'reviewed'
        })
      });

      if (response.ok) {
        setShowResponseModal(false);
        setSelectedFeedback(null);
        setResponseText('');
        loadAllData();
      }
    } catch (error) {
      console.error('Error sending response:', error);
    }
  };

  const handleDelete = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.FEEDBACK}/${feedbackId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadAllData();
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  // ========================= FILTERING =========================
  const getFilteredFeedback = () => {
    switch (activeTab) {
      case 'pending':
        return feedback.filter(f => f.status === 'pending');
      case 'reviewed':
        return feedback.filter(f => f.status === 'reviewed');
      case 'resolved':
        return feedback.filter(f => f.status === 'resolved');
      case 'complaints':
        return feedback.filter(f => f.type === 'complaint');
      case 'ideas':
        return feedback.filter(f => f.type === 'idea');
      default:
        return feedback;
    }
  };

  // ========================= HELPERS =========================
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    return type === 'complaint' ? 'Warning' : 'Idea';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'facilities': return 'Facilities';
      case 'academic': return 'Books';
      case 'activities': return 'Goals';
      case 'food': return 'Food';
      case 'transport': return 'Transport';
      case 'general': return 'Note';
      default: return 'Note';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading feedback management...</div>
      </div>
    );
  }

  const filteredFeedback = getFilteredFeedback();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Feedback Management</h1>
              <p className="text-sm text-gray-600">Review and respond to student feedback and suggestions</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">📬</div>
            <h3 className="text-lg font-semibold text-gray-800">{stats.total || 0}</h3>
            <p className="text-sm text-gray-600">Total Feedback</p>
          </div>

          <div className="bg-yellow-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Loading</div>
            <h3 className="text-lg font-semibold text-yellow-800">{stats.pending || 0}</h3>
            <p className="text-sm text-yellow-600">Pending</p>
          </div>

          <div className="bg-blue-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">👁️</div>
            <h3 className="text-lg font-semibold text-blue-800">{stats.reviewed || 0}</h3>
            <p className="text-sm text-blue-600">Reviewed</p>
          </div>

          <div className="bg-red-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Warning</div>
            <h3 className="text-lg font-semibold text-red-800">{stats.complaints || 0}</h3>
            <p className="text-sm text-red-600">Complaints</p>
          </div>

          <div className="bg-green-50 rounded-lg shadow p-6 text-center">
            <div className="text-3xl mb-2">Idea</div>
            <h3 className="text-lg font-semibold text-green-800">{stats.ideas || 0}</h3>
            <p className="text-sm text-green-600">Ideas</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All Feedback', count: stats.total || 0 },
              { key: 'pending', label: 'Pending', count: stats.pending || 0 },
              { key: 'reviewed', label: 'Reviewed', count: stats.reviewed || 0 },
              { key: 'resolved', label: 'Resolved', count: stats.resolved || 0 },
              { key: 'complaints', label: 'Complaints', count: stats.complaints || 0 },
              { key: 'ideas', label: 'Ideas', count: stats.ideas || 0 }
            ].map(filter => (
              <button
                key={filter.key}
                onClick={() => setActiveTab(filter.key)}
                className={`px-4 py-2 rounded-md font-medium ${
                  activeTab === filter.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>
        </div>

        {/* Feedback List */}
        <div className="space-y-4">
          {filteredFeedback.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="text-6xl mb-4">Inbox</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Feedback Found</h3>
              <p className="text-gray-600">
                {activeTab === 'all'
                  ? 'No feedback has been submitted yet.'
                  : `No ${activeTab} feedback found.`}
              </p>
            </div>
          ) : (
            filteredFeedback.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl">{getTypeIcon(item.type)}</span>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{item.subject}</h3>
                          <div className="flex items-center space-x-3 text-sm text-gray-600">
                            <span>By {item.anonymous ? 'Anonymous' : item.studentName}</span>
                            <span>?</span>
                            <span>{item.className}</span>
                            <span>?</span>
                            <span className="flex items-center space-x-1">
                              <span>{getCategoryIcon(item.category)}</span>
                              <span>{item.category}</span>
                            </span>
                            <span>?</span>
                            <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                          {item.priority} priority
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      {item.status === 'pending' && (
                        <button
                          onClick={() => handleStatusChange(item.id, 'reviewed')}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                        >
                          Mark Reviewed
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setSelectedFeedback(item);
                          setResponseText(item.adminResponse || '');
                          setShowResponseModal(true);
                        }}
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                      >
                        {item.adminResponse ? 'Update Response' : 'Respond'}
                      </button>

                      <button
                        onClick={() => handleDelete(item.id)}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-gray-800 leading-relaxed">{item.message}</p>
                  </div>

                  {/* Admin Response */}
                  {item.adminResponse && (
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-blue-600 font-medium">👨‍School Admin Response</span>
                        <span className="text-xs text-gray-500">
                          {new Date(item.respondedAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-blue-800">{item.adminResponse}</p>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Response Modal */}
      {showResponseModal && selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Respond to Feedback</h3>

              {/* Original Feedback */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">{selectedFeedback.subject}</h4>
                <p className="text-gray-600 text-sm mb-2">
                  From {selectedFeedback.anonymous ? 'Anonymous' : selectedFeedback.studentName} ? {selectedFeedback.className}
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800">{selectedFeedback.message}</p>
                </div>
              </div>

              {/* Response Form */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Response <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  rows="6"
                  placeholder="Enter your response to this feedback..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Modal Actions */}
              <div className="flex space-x-3 pt-6 border-t">
                <button
                  onClick={handleRespond}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                >
                  Send Response
                </button>
                <button
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedFeedback(null);
                    setResponseText('');
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFeedback;
